import { isArray, isNonEmptyString, isObject, isString } from '@utils/type-checkers'


export interface SuccessResponse { status: 'successful' }
export interface FailedResponse {
    status: 'failed',
    type: string
    name: string
    message: string
}

/**
 * @description Formats response data into a `SuccessResponse` or `FailedResponse` while ensuring that certain values are always set.
 * 
 * @param {Response} data Potential object to format.
 * @returns {Response} `SuccessResponse` or `FailedResponse` object
 */
export const createResponseObj = <Response extends (SuccessResponse | FailedResponse)>(data: Response): Response => {
    switch (data?.status) {
        // ToDo: Better response handling. (response.ok?)
        // Temp extra case to handle typos. Casting a literal to another literal makes me feel dirty...
        case 'successful': case 'success' as 'successful': return { ...data, status: 'successful' }
        case 'failed': return { ...data, status: 'failed', type: data?.type || '', name: data?.name || '', message: data?.message || '' }
        default: return {
            ...data,
            status: 'failed',
            type: (data as FailedResponse)?.type || '',
            name: (data as FailedResponse)?.name || '',
            message: (data as FailedResponse)?.message || '',
        }
    }
}

export const isFailedResponse = (value: unknown): value is FailedResponse => (
    isObject(value)
    && value.status === 'failed'
    && isString(value.type)
    && isString(value.name)
    && isString(value.message)
)

export const getJwtToken = async (): Promise<string> => {
    try {
        const ClerkApi = window.Clerk
        if (!ClerkApi || !ClerkApi.loaded) throw new Error("Clerk isn't loaded");
        return await ClerkApi.session?.getToken({ template: 'user_session_template' }) || ''
    } catch (error) {
        console.error(error)
        return ''
    }
}

type CustomHeader = typeof customHeaders[number]
const customHeaders = ['Clerk-Token'] as const
const customHeaderValue: Record<CustomHeader, () => Promise<string>> = {
    'Clerk-Token': async () => `Bearer ${await getJwtToken()}`
}
const isCustomHeader = (value: unknown): value is CustomHeader => (
    isNonEmptyString(value)
    && !!customHeaders.find((header) => (header === value))
)


export const generateHeaders = async (...args: (CustomHeader | HeadersInit | undefined)[]): Promise<Headers> => {
    // ToDo: Setup Header overrides
    const headers = new Headers({ 'Content-Type': 'application/json' })

    for (const arg of args) {
        if (!arg) break;
        if (isCustomHeader(arg)) headers.append(arg, await customHeaderValue[arg]());
        else if (arg instanceof Headers) arg.forEach((value, key) => { headers.append(key, value) });
        else if (isArray(arg)) arg.forEach((subArr) => { headers.append(subArr[0], subArr[1]) });
        else if (isObject(arg)) Object.keys(arg).forEach((key) => { headers.append(key, arg[key]) })
    }

    return headers
}

export * as AuthAPI from './auth'
export * as EvervaultAPI from './evervault'
export * as PersonaAPI from './persona'
export * as StripeAPI from './stripe'
export * as TeraAPI from './tera'
export * as VirtualCardAPI from './virtual-card'